// src/components/roads/drive/DetectionImage.js
// --- UPDATE (Drive Mode AI review) -----------------------------------------------------
// A Drive Mode photo with the AI's boxes drawn on it (normalised [cx, cy, w, h] from the phone).
// The wrapper takes the photo's own aspect ratio, so the boxes line up exactly at any size.
// --- END UPDATE ------------------------------------------------------------------------
import React, { memo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Icon, Text, Spinner, useColorModeValue } from '@chakra-ui/react';
import { MdBrokenImage, MdZoomIn } from 'react-icons/md';
import { damageInfo, photoSrc, pct } from './driveAiApi';

const DetectionImage = ({ finding, showBoxes = true, showLabels = true, onClick, maxH, rounded = 'lg', highlightType }) => {
    const [ratio, setRatio] = useState(4 / 3);
    const [state, setState] = useState('loading'); // loading | ok | error
    const placeholderBg = useColorModeValue('gray.100', 'whiteAlpha.100');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const src = photoSrc(finding?.photo_url);
    const onLoad = useCallback((e) => {
        const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
        if (w && h) setRatio(w / h);
        setState('ok');
    }, []);
    const boxes = (finding?.detections || []).filter((d) => Array.isArray(d.box) && d.box.length === 4);

    if (!src) {
        return (
            <Flex bg={placeholderBg} borderRadius={rounded} align="center" justify="center" direction="column" py={8} gap={1} onClick={onClick} cursor={onClick ? 'pointer' : 'default'}>
                <Icon as={MdBrokenImage} boxSize={6} color={muted} />
                <Text fontSize="2xs" color={muted}>No photo sent</Text>
            </Flex>
        );
    }

    return (
        <Box position="relative" w="100%" maxW={maxH ? `calc(${maxH} * ${ratio})` : undefined} mx="auto"
            style={{ aspectRatio: String(ratio) }} borderRadius={rounded} overflow="hidden" bg={placeholderBg}
            onClick={onClick} cursor={onClick ? 'zoom-in' : 'default'} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}
            onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
            aria-label={onClick ? `Open photo: ${damageInfo(finding?.damage_type).label}` : `${damageInfo(finding?.damage_type).label} photo`}
            sx={{ '&:hover .zoom-hint': { opacity: 1 } }}>
            <img src={src} alt={damageInfo(finding?.damage_type).label} onLoad={onLoad} onError={() => setState('error')} loading="lazy"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'fill', display: state === 'error' ? 'none' : 'block' }} />
            {state === 'loading' && <Flex position="absolute" inset={0} align="center" justify="center"><Spinner size="sm" /></Flex>}
            {state === 'error' && (
                <Flex position="absolute" inset={0} align="center" justify="center" direction="column" gap={1}>
                    <Icon as={MdBrokenImage} boxSize={6} color={muted} />
                    <Text fontSize="2xs" color={muted}>Photo not found on the server</Text>
                </Flex>
            )}
            {state === 'ok' && showBoxes && boxes.length > 0 && (
                <>
                    <svg viewBox="0 0 1 1" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} aria-hidden="true">
                        {boxes.map((d, i) => {
                            const [cx, cy, w, h] = d.box;
                            const c = damageInfo(d.label).color;
                            const dim = highlightType && d.label !== highlightType;
                            return (
                                <rect key={i} x={cx - w / 2} y={cy - h / 2} width={w} height={h} fill={c} fillOpacity={dim ? 0.02 : 0.12}
                                    stroke={c} strokeWidth={dim ? 1.5 : 2.5} strokeOpacity={dim ? 0.5 : 1} vectorEffect="non-scaling-stroke" rx={0.004} />
                            );
                        })}
                    </svg>
                    {showLabels && boxes.map((d, i) => {
                        const [cx, cy, w, h] = d.box;
                        const top = Math.max(0, cy - h / 2);
                        return (
                            <Box key={`l${i}`} position="absolute" left={`${Math.max(0, Math.min(0.98, cx - w / 2)) * 100}%`} top={`${top * 100}%`}
                                transform={top > 0.06 ? 'translateY(-100%)' : 'none'} bg={damageInfo(d.label).color} color="white"
                                fontSize="10px" fontWeight="800" px={1} borderRadius="sm" whiteSpace="nowrap" pointerEvents="none" lineHeight="1.5">
                                {damageInfo(d.label).label} {pct(d.confidence)}
                            </Box>
                        );
                    })}
                </>
            )}
            {onClick && state === 'ok' && (
                <Flex className="zoom-hint" position="absolute" right={1.5} bottom={1.5} bg="blackAlpha.600" color="white" borderRadius="md" p={1} opacity={0} transition="opacity 0.15s">
                    <Icon as={MdZoomIn} boxSize={4} />
                </Flex>
            )}
        </Box>
    );
};

DetectionImage.propTypes = {
    finding: PropTypes.object,
    showBoxes: PropTypes.bool,
    showLabels: PropTypes.bool,
    onClick: PropTypes.func,
    maxH: PropTypes.string,
    rounded: PropTypes.string,
    highlightType: PropTypes.string,
};

export default memo(DetectionImage);
